import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useClient } from 'shared/apollo';
import { FeedbackSectionId } from './utils';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { AnswersBySectionIdResponse, getAnswersByQuestionId } from './api';
import { SectionIndicators } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import { parseImpactSummaryAnswer } from 'npo/pages/NPOFeedback/FeedbackDetails/ImpactSummaryDetails';
import {
  IndicatorByService,
  indicatorSectionsInOrder,
  IndicatorWithUpdate,
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import { IMPACT_SUMMARY_QUESTION_ID } from 'npo/pages/NPOFeedback/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCalculator,
  faFileLines,
  faHashtag,
  faMap,
} from '@fortawesome/pro-light-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { RenderStageIcon } from './FeedbackImpactDetails/ImpactDetailStageIcons';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { TrueImpactIndicatorFilesRender } from 'shared/design-system/components/TrueImpactFileRender';
import { useIndicator } from 'shared/providers/IndicatorProvider';

const flattenImpactSummaryIndicators = (i: IndicatorByService | undefined) => {
  const result: { [key: string]: IndicatorWithUpdate } = {};
  if (!i) {
    return result;
  }
  indicatorSectionsInOrder.forEach(section => {
    i[section]?.forEach(indicator => {
      result[indicator.id] = indicator;
    });
  });
  return result;
};

interface RenderModelProps {
  reportId: number;
  questionId: number;
  sectionId: FeedbackSectionId;
  indicatorDetails: SectionIndicators;
}

const StyledDiv = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${SPACERS.small};
  .render-model-indicator {
    border-radius: 4px;
    padding: ${SPACERS.small};
    border: 1px solid ${COLORS.grayBase};
  }
  .render-model-indicator-margin {
    margin-top: ${SPACERS.small};
  }
`;

const RenderModel: React.FC<RenderModelProps> = ({
  indicatorDetails,
  reportId,
  questionId,
  sectionId,
}) => {
  const client = useClient();
  const { getSourcesAndAssumptions } = useIndicator();
  const [request, makeRequest] = useGraphQLRequest<
    AnswersBySectionIdResponse
  >();

  useEffect(() => {
    if (client && request.type === 'REQUEST_INIT') {
      makeRequest(
        getAnswersByQuestionId(client, reportId, IMPACT_SUMMARY_QUESTION_ID),
      );
    }
  }, [request]);

  const impactSummaryIndicators = flattenImpactSummaryIndicators(
    parseImpactSummaryAnswer(request.data?.allNpoReportAnswers[0]?.text),
  );

  return (
    <StyledDiv>
      {indicatorSectionsInOrder.map(s => {
        if (s === 'catalyzed' || indicatorDetails[s].length === 0) return null;
        return (
          <div key={s} className="render-model-section">
            <RenderStageIcon sectionKey={s} />
            {indicatorDetails[s].map((indicator, index) => {
              const summaryIndicator =
                impactSummaryIndicators[indicator.indicatorId];
              const description = getSourcesAndAssumptions(indicator);
              return (
                <div
                  key={indicator.indicatorId}
                  className={
                    index === 0
                      ? 'render-model-indicator'
                      : 'render-model-indicator render-model-indicator-margin'
                  }
                >
                  <div>{indicator.indicatorName}</div>
                  <div>
                    <FontAwesomeIcon
                      color={COLORS.ctaPrimary}
                      icon={faHashtag}
                    />{' '}
                    {numberWithCommas(indicator.forecast)}
                    {indicator.finalForecast ? (
                      <>
                        {' '}
                        <FontAwesomeIcon
                          color={COLORS.ctaPrimary}
                          icon={faArrowRight}
                        />{' '}
                        {numberWithCommas(indicator.finalForecast)}
                      </>
                    ) : (
                      ''
                    )}
                  </div>
                  <div>
                    <FontAwesomeIcon
                      color={COLORS.ctaPrimary}
                      icon={faFileLines}
                    />{' '}
                    {summaryIndicator?.updatedText || ''}
                  </div>
                  <div>
                    <FontAwesomeIcon
                      color={COLORS.ctaPrimary}
                      icon={faCalculator}
                    />{' '}
                    {indicator.measurement}
                  </div>
                  <div>
                    <FontAwesomeIcon color={COLORS.ctaPrimary} icon={faMap} />{' '}
                    {description || <i>Not provided</i>}
                  </div>
                  <TrueImpactIndicatorFilesRender indicator={indicator} />
                </div>
              );
            })}
          </div>
        );
      })}
    </StyledDiv>
  );
};

export default RenderModel;
